import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useBlocker, useParams } from 'react-router-dom';

import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import classNames from 'classnames';
import { useShallow } from 'zustand/shallow';

import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { LoadingState } from '@/shared/ui/states';
import { TabLabel } from '@/shared/ui/TabLabel';

import { type DagDetailTabKey, useDagDetailTabsStore } from '@/entities/dag';

import { DagTagSection } from './_internal/DagTagSection';
import styles from './DagDetailTabs.module.css';

const DagDetailOverviewTabLazy = lazy(() =>
  import('@/features/dag-detail/ui/DagDetailOverviewTab/DagDetailOverviewTab').then((m) => ({
    default: m.DagDetailOverviewTab,
  }))
);

const DagDetailRunsTabLazy = lazy(() =>
  import('@/features/dag-detail/ui/DagDetailRunsTab/DagDetailRunsTab').then((m) => ({
    default: m.DagDetailRunsTab,
  }))
);

const DagDetailCodeTabLazy = lazy(() =>
  import('@/features/dag-detail/ui/DagDetailCodeTab/DagDetailCodeTab').then((m) => ({
    default: m.DagDetailCodeTab,
  }))
);

type LazyTabContentProps = {
  children: ReactNode;
};

const LazyTabContent = ({ children }: LazyTabContentProps) => {
  return (
    <ErrorBoundary
      FallbackComponent={(fallbackProps) => (
        <ErrorFallback className={styles.errorBoundary} variant="compact" {...fallbackProps} />
      )}>
      <Suspense
        fallback={
          <div className={styles.loadingContainer}>
            <LoadingState compact />
          </div>
        }>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

export const DagDetailTabs = () => {
  const params = useParams<{ dagId?: string }>();
  const dagId = params.dagId;

  const { currentTab, isExpanded, setCurrentTab, toggleExpanded, resetTabsState } = useDagDetailTabsStore(
    useShallow((state) => ({
      currentTab: state.currentTab,
      isExpanded: state.isExpanded,
      setCurrentTab: state.setCurrentTab,
      toggleExpanded: state.toggleExpanded,
      resetTabsState: state.resetTabsState,
    }))
  );
  const [hasUnsavedCodeChanges, setHasUnsavedCodeChanges] = useState(false);
  const [pageLeaveConfirmOpen, setPageLeaveConfirmOpen] = useState(false);
  const [tabLeaveConfirmOpen, setTabLeaveConfirmOpen] = useState(false);
  const [pendingTabKey, setPendingTabKey] = useState<DagDetailTabKey | null>(null);
  const shouldBlockRef = useRef(true);

  const handleTabChange = useCallback(
    (activeKey: string) => {
      const nextTab = activeKey as DagDetailTabKey;

      if (currentTab === nextTab) {
        return;
      }

      if (currentTab === 'Code' && hasUnsavedCodeChanges) {
        setPendingTabKey(nextTab);
        setTabLeaveConfirmOpen(true);
        return;
      }

      setCurrentTab(nextTab);
    },
    [currentTab, hasUnsavedCodeChanges, setCurrentTab]
  );

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!shouldBlockRef.current || !hasUnsavedCodeChanges || currentTab !== 'Code') {
      return false;
    }

    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }

    return true;
  });

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockRef.current && hasUnsavedCodeChanges && currentTab === 'Code') {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentTab, hasUnsavedCodeChanges]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setPageLeaveConfirmOpen(true);
    }
  }, [blocker]);

  useEffect(() => {
    return () => {
      resetTabsState();
    };
  }, [resetTabsState]);

  const handlePageLeaveConfirm = useCallback(() => {
    setHasUnsavedCodeChanges(false);
    shouldBlockRef.current = false;
    blocker.proceed?.();
  }, [blocker]);

  const handlePageLeaveCancel = useCallback(() => {
    setPageLeaveConfirmOpen(false);
    blocker.reset?.();
  }, [blocker]);

  const handleTabLeaveConfirm = useCallback(() => {
    if (!pendingTabKey) {
      return;
    }

    setHasUnsavedCodeChanges(false);
    setCurrentTab(pendingTabKey);
  }, [pendingTabKey, setCurrentTab]);

  const handleTabLeaveCancel = useCallback(() => {
    setPendingTabKey(null);
    setTabLeaveConfirmOpen(false);
  }, []);

  const tabItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'Task',
        label: <TabLabel label="작업" showCount={false} active={currentTab === 'Task'} />,
        children: (
          <LazyTabContent>
            <DagDetailOverviewTabLazy />
          </LazyTabContent>
        ),
      },
      {
        key: 'Execution Record',
        label: <TabLabel label="실행 기록" showCount={false} active={currentTab === 'Execution Record'} />,
        children: (
          <LazyTabContent>
            <DagDetailRunsTabLazy />
          </LazyTabContent>
        ),
      },
      {
        key: 'Code',
        label: <TabLabel label="코드" showCount={false} active={currentTab === 'Code'} />,
        children: (
          <LazyTabContent>
            <DagDetailCodeTabLazy onUnsavedChangesChange={setHasUnsavedCodeChanges} />
          </LazyTabContent>
        ),
      },
    ],
    [currentTab]
  );

  return (
    <div className={styles.content}>
      <Tabs
        activeKey={currentTab}
        onChange={handleTabChange}
        items={tabItems}
        className={styles.tabs}
        destroyOnHidden
        tabBarExtraContent={{
          right: currentTab === 'Code' && (
            <IconButton
              className={styles.expandButton}
              size="regular"
              ariaLabel={isExpanded ? '코드 영역 축소' : '코드 영역 확대'}
              onClick={toggleExpanded}>
              <Icon name={isExpanded ? 'collapse' : 'expand'} size={16} />
            </IconButton>
          ),
        }}
      />
      <ConfirmModal
        open={pageLeaveConfirmOpen}
        message={
          <>
            작성중이던 정보가 유실될 수 있습니다.
            <br />
            이동하시겠습니까?
          </>
        }
        confirmText="이동"
        cancelText="취소"
        onConfirm={handlePageLeaveConfirm}
        onCancel={handlePageLeaveCancel}
      />
      <ConfirmModal
        open={tabLeaveConfirmOpen}
        message={
          <>
            작성중이던 정보가 유실될 수 있습니다.
            <br />
            이동하시겠습니까?
          </>
        }
        confirmText="이동"
        cancelText="취소"
        onConfirm={handleTabLeaveConfirm}
        onCancel={handleTabLeaveCancel}
      />
      <div
        className={classNames(styles.tagSection, {
          [styles.tagSectionCollapsed]: isExpanded,
          [styles.tagSectionExpanded]: !isExpanded,
        })}
        aria-hidden={isExpanded}>
        <ErrorBoundary FallbackComponent={(fallbackProps) => <ErrorFallback variant="compact" {...fallbackProps} />}>
          <Suspense
            fallback={
              <div className={styles.loadingContainer}>
                <LoadingState compact />
              </div>
            }>
            <DagTagSection dagId={dagId ?? ''} />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};
